import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { getAPI, reinitialiseAPI } from 'store/apiReducer';
import * as PropTypes from 'prop-types';
import {
    Alert,
    Button,
    Col,
    Container,
    InputControl,
    PaginationControl,
    Row,
    SegmentLoader,
} from '@design-system-etat/dsfr-react';
import { useForm } from 'react-hook-form';
import usagerClientReducer from '../../../../store/usagerReducer';
import {
    API_GET_HISTORIQUE_ARMES,
    CONTEXT_GET_HISTORIQUE_ARMES,
} from '../../../../constants/apiUrls.constants';
import AffichageResponsive from '../../../../components/Commun/AffichageResponsive';
import TableauHistorique from './TableauHistorique';
import CarteResultat from './CarteResultat';

const Historique = ({ getApi, isLoading, data, isLoadingArme }) => {
    const [champRecherche, setChampRecherche] = useState('');
    const [ordreTri, setOrdreTri] = useState('DESC');
    const [champTri, setChampTri] = useState('dateOperation');
    const [page, setPage] = useState(1);

    const form = useForm();

    useEffect(() => {
        let suffixeUrlRecherche = '';
        suffixeUrlRecherche += `?_sort=${champTri}`;
        suffixeUrlRecherche += `&_order=${ordreTri}`;
        suffixeUrlRecherche += `&_page=${page}`;
        if (champRecherche) {
            suffixeUrlRecherche += `&recherche=${champRecherche}`;
        }

        getApi(
            usagerClientReducer,
            API_GET_HISTORIQUE_ARMES + suffixeUrlRecherche,
            CONTEXT_GET_HISTORIQUE_ARMES,
        );
    }, [ordreTri, champTri, champRecherche, page]);

    const nombrePage = useMemo(() => {
        if (!data) {
            return page;
        }

        // Calcul du nombre de pages.
        const totalPage = Math.ceil(data.nombreTotal / data.pageSize);

        /**
         * Si on a zéro page, on retourne qu'il n'y a qu'une seule page. Ceci étant pour empêcher un dysfonctionnement
         * du design.
         */
        if (totalPage === 0) {
            return 1;
        }

        return totalPage;
    }, [data]);

    function onSubmit(dataForm) {
        setPage(1);
        setChampRecherche(dataForm.recherche);
    }
    if (data?.nombreTotal === 0 && !champRecherche) {
        return (
            <Alert severity="info">Aucune donnée d'historique est présente pour votre compte</Alert>
        );
    }

    return (
        <SegmentLoader isLoading={isLoading || isLoadingArme}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Row verticalAlign="bottom" className="fr-mb-3w">
                    <Col col={6}>
                        <InputControl
                            id="RechercheArme"
                            label="Rechercher une arme"
                            hintText="Vous pouvez rechercher sur l'ensemble des colonnes, mis à part la colonne Évènement"
                            {...form.register('recherche')}
                        />
                    </Col>
                    <Col col={6} className="fr-pl-3w">
                        <Button type="submit">Rechercher</Button>
                    </Col>
                </Row>
            </form>
            {data?.nombreTotal === 0 && champRecherche && (
                <p className="fr-error" role="alert">
                    Aucun résultat pour votre recherche
                </p>
            )}
            {data?.nombreTotal > 0 && (
                <>
                    <AffichageResponsive
                        pointDeRupture="xl"
                        composantDesktop={
                            <TableauHistorique
                                armes={data?.records ?? []}
                                ordreTri={ordreTri}
                                setOrdreTri={setOrdreTri}
                                champTri={champTri}
                                setChampTri={setChampTri}
                            />
                        }
                        composantMobile={
                            <Container fluid={false}>
                                <Row className="liste-resultats-mobile">
                                    {(data?.records ?? []).map((historique) => (
                                        <CarteResultat historique={historique} />
                                    ))}
                                </Row>
                            </Container>
                        }
                    />
                    <Row horizontalAlign="center">
                        <PaginationControl
                            currentPage={page}
                            onChangePage={setPage}
                            totalPage={nombrePage}
                        />
                    </Row>
                </>
            )}
        </SegmentLoader>
    );
};

Historique.propTypes = {
    getApi: PropTypes.func.isRequired,
    data: PropTypes.shape({
        nombreTotal: PropTypes.number.isRequired,
        pageSize: PropTypes.number.isRequired,
        records: PropTypes.array,
    }),
    isLoading: PropTypes.bool,
    isLoadingArme: PropTypes.bool,
    dataProfil: PropTypes.shape({
        roles: PropTypes.array.isRequired,
    }),
};

export default connect(
    (state) => {
        const { data: dataProfil } = state.profil.usager;
        const { isLoading: isLoadingFicheSituation, data: dataFicheSituation } =
            state.armes.ficheSituationArme;
        const { isLoading, data } = state.historique.armes;
        const { isLoading: isLoadingArme } = state.recherche.armeLoader;
        return {
            isLoading,
            data,
            isLoadingArme,
            dataProfil,
            dataFicheSituation,
            isLoadingFicheSituation,
        };
    },
    {
        getApi: getAPI,
        reinitialiseApi: reinitialiseAPI,
    },
)(Historique);
